/**
 * Server-side call creation (SCH-613).
 *
 *   POST /v1/calls   member, x-school-id
 *
 * The backend authorizes the call (module on, contact matrix, student↔student
 * ban, staff presence — decisions doc `2026-10-13-001` D2), creates it on
 * Stream Video as the caller with the members it read from the chat channel,
 * and writes the audit record. The app then rings it with
 * `call.get({ ring: true })` and `POST /v1/calls/:callCid/ring`; it never
 * creates a call itself.
 *
 * A refusal is HTTP 403 with `error.type = 'CALL_NOT_PERMITTED'` and
 * `error.details.reason` one of {@link CallRefusalReasonSchema}; a school
 * without the module gets `error.type = 'CALLS_DISABLED'`.
 */

import { z } from 'zod'

/** Stream call id the app generates (`newCallId()`); the cid is `default:<callId>`. */
export const CallIdSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(/^[A-Za-z0-9_-]+$/, 'callId must be URL-safe')

/** Stream Chat channel cid, `type:id` (e.g. `messaging:abc`). */
export const ChannelCidSchema = z
  .string()
  .min(3)
  .max(200)
  .regex(/^[a-z0-9_-]+:[A-Za-z0-9_!-]+$/, 'channelCid must be a Stream channel cid (type:id)')

export const CreateCallRequestSchema = z.object({
  callId: CallIdSchema,
  /** The chat channel the call starts from. Members come from it, server-side. */
  channelCid: ChannelCidSchema,
  /** Start with the camera available. Refused when the school disallows video. */
  video: z.boolean().default(false)
})
export type CreateCallRequest = z.infer<typeof CreateCallRequestSchema>

/** Why the server refused a call. One per decisions-doc rule (D2). */
export const CallRefusalReasonSchema = z.enum([
  /** C1: the school's `call` capability is off, or the caller's role is not in it. */
  'module_disabled',
  /** C1: the call asked for video and the school allows voice only. */
  'video_disabled',
  /** C2: the caller or a callee is not an ACTIVE member of this school. */
  'not_active_member',
  /** The caller is not a member of the chat channel. */
  'not_channel_member',
  /** The channel has no other ACTIVE member to call. */
  'no_callees',
  /** C3: the chat contact matrix does not let the caller reach the callee. */
  'contact_not_permitted',
  /** C4: student↔student. */
  'student_to_student',
  /** C5: a parent and a student who are not parent and child. */
  'parent_not_related',
  /** C7: the call includes a student and no staff member. */
  'staff_presence_required',
  /** The channel has more members than a call allows. */
  'too_many_members'
])
export type CallRefusalReason = z.infer<typeof CallRefusalReasonSchema>

/** The most members a call may have (Stream ring ceiling, `RING_MEMBERS_LIMIT`). */
export const CALL_MAX_MEMBERS = 100

export const CreateCallResponseSchema = z.object({
  message: z.string(),
  data: z.object({
    callType: z.literal('default'),
    callId: z.string(),
    /** `default:<callId>` */
    callCid: z.string(),
    /** Stream user ids (UsersOnSchools.publicId) the call was created with, caller included. */
    memberIds: z.array(z.string()),
    /** Whether the call will be recorded (school setting). The app shows the notice. */
    recording: z.boolean(),
    /** The audit record's public id. */
    callRecordId: z.string()
  })
})
export type CreateCallResponse = z.infer<typeof CreateCallResponseSchema>

/** Shape of `error.details` on a 403 CALL_NOT_PERMITTED. */
export const CallRefusalDetailsSchema = z.object({
  reason: CallRefusalReasonSchema,
  /** Stream user ids the refusal is about, when it is about specific callees. */
  userIds: z.array(z.string()).optional()
})
export type CallRefusalDetails = z.infer<typeof CallRefusalDetailsSchema>
